/**
 * i18n messages for the Review Requests module. Registered in
 * core/plugins/i18n.ts alongside the reports, expense-tracker and
 * certification bundles.
 *
 * CSP-safe compiler: only `{named}` placeholders — no plurals, no linked
 * messages (`@:`). That constraint is why count-dependent text below appears
 * as `_one` / `_other` sibling keys chosen by the caller, rather than
 * vue-i18n's `|` plural syntax, which `cspSafeMessageCompiler` does not
 * implement.
 *
 * **English only, deliberately.** Every other bundle here ships `en` + `ar`;
 * this one does not, and that is a decision rather than an omission. These
 * five screens are internal SRB administration — they price reviews and set
 * commercial terms, and they are never rendered to a client. Machine-quality
 * Arabic for ~180 strings of Shariah-review commercial vocabulary would be
 * worse than the clean English fallback vue-i18n already gives (no
 * `fallbackLocale` is configured, so it falls back to `en`): a mistranslated
 * arrangement name or seed warning on these screens is a commercial error,
 * not a cosmetic one. Add an `ar` bucket here when a native speaker can
 * review it — nothing else has to change.
 *
 * **Two groups live in sibling files** and are spread back in below:
 * `reviewRequests.bands.ts` (the Category Bands screen and its seed panel) and
 * `reviewRequests.composer.ts` (the request lifecycle — composer, explorer,
 * list, detail; which in turn spreads in `reviewRequests.scholars.ts`, the
 * composer's scholars step). That is a FILE split only, done in Phase 2B Task 8 to get
 * this file back under the repo's 400-line hard limit: the keys keep their
 * exact paths (`reviewRequests.bands.*`, `reviewRequests.requests.*`) and
 * `@/locales/reviewRequests` remains the one import path — nothing that reads
 * a message changed. Add new keys to whichever file owns the screen.
 */
import { reviewRequestsBandsMessages } from '@/locales/reviewRequests.bands'
import { reviewRequestsComposerMessages } from '@/locales/reviewRequests.composer'

export const reviewRequestsMessages = {
  en: {
    reviewRequests: {
      // The tab strip on `ReviewRequestsView.vue`. Separate from each
      // screen's own `breadcrumb`/`title`: a tab label sits beside its
      // sibling and has to read as a choice between the two, which is why
      // "Requests Dashboard" is longer here than the breadcrumb above the
      // list itself.
      nav: {
        requests: 'Requests Dashboard',
        bands: 'Category Bands',
      },

      common: {
        save: 'Save',
        saving: 'Saving…',
        cancel: 'Cancel',
        selectScholar: 'Select a scholar…',
        errorTitle: 'Error',
        savedTitle: 'Saved',
        addedTitle: 'Added',
        couldNotSaveTitle: 'Could Not Save',
        unexpectedError: 'An unexpected error occurred. Please try again.',
        sessionExpired: 'Your session has expired. Please sign in again.',
        clientNotFound: 'This client could not be found.',
        clientNotFoundPanel:
          'This client could not be found. It may be outside your sector, or it may not exist.',
        scholarListLoadFailed:
          'The scholar list could not be loaded. Refresh the page and try again.',
        scholarNotFound: 'That scholar could not be found. Refresh the page and try again.',
        classificationNotFound:
          'That classification could not be found. Refresh the page and try again.',
        /** Fallback display name when a CSA-synced scholar row has neither a
         *  short name nor usable name parts (`rosterRules.ts`). */
        scholarIdFallback: '#{id}',
      },

      validators: {
        currency: 'Enter exactly three letters, e.g. USD.',
        anchorDay: 'Enter a day between 1 and 28.',
      },

      settings: {
        breadcrumb: 'Client Review Settings',
        // Names what the toggle below actually does, rather than restating
        // the tab that hosts it: the whole panel is the Odoo purchase-order
        // trigger. Doubles as the sub-section heading on the client form,
        // where the tab strip already says "Review Settings".
        title: 'Odoo Purchase Order Activation',
        titleFor: 'Odoo Purchase Order Activation — {client}',
        loadError: 'Could not load client review settings. Please try again.',
        poTriggerLabel: 'Raise purchase orders in Odoo',
        poTriggerHint:
          'When this is on, every scholar on a review request for this client raises one purchase order in Odoo — including SRB employees, at zero value.',
        lastChanged: 'Last changed {when}',
        notConfigured: 'Not yet configured for this client — showing defaults.',
        savedMessage: 'Client review settings updated.',
        status: {
          conflict:
            'Purchase orders cannot be switched on until the Odoo connection is configured.',
          forbidden: 'You do not have permission to change these settings.',
          unprocessable: 'Check the values on this form and try again.',
          fallback: 'Could not save the client review settings. Please try again.',
        },
      },

      roster: {
        breadcrumb: 'Client Roster',
        title: 'SSB Roster',
        titleFor: 'SSB Roster — {client}',
        /** The heading when this panel is a SUB-SECTION of the client form's
         *  Review Settings tab. Names the section, not the client: the form's
         *  own page heading already names the client, and repeating it on
         *  every sub-section reads as noise. */
        sectionTitle: 'Review Roster',
        loadError: 'Could not load the client roster. Please try again.',
        introBefore:
          "Every scholar sitting on this client's Shariah Supervisory Board. Rows marked",
        introMiddle: 'were derived from past certifications and may need correcting; rows marked',
        introAfter: 'were asserted by a person.',
        currentMembers: 'Current members',
        history: 'History',
        historyNote: 'Ended memberships stay here as the audit trail rather than being deleted.',
        sourceSeeded: 'From certification history',
        sourceManual: 'Added by hand',
        emptyCurrent: "No scholars are currently on this client's Shariah Supervisory Board.",
        emptyClosed: 'No ended memberships yet.',
        captionCurrent: "Scholars currently on this client's Shariah Supervisory Board",
        captionClosed: "Scholars formerly on this client's Shariah Supervisory Board",
        colScholar: 'Scholar',
        colSource: 'Source',
        colMemberSince: 'Member since',
        colWindow: 'Membership window',
        colActions: 'Actions',
        windowRange: '{from} until {to}',
        endMembership: 'End membership',
        endMembershipAria: "End {scholar}'s membership",
        endingConfirmText:
          'This ends the membership from today. The record stays, so past reviews still make sense.',
        ending: 'Ending…',
        confirm: 'Confirm',
        addLabel: 'Add a scholar',
        add: 'Add',
        adding: 'Adding…',
        addHint: 'A scholar added here is recorded as added by hand, not derived from history.',
        addHintAllPresent: 'Every scholar is already an active member of this roster.',
        reopenWarning:
          'This reopens their membership from today. Their previous window ({from} – {to}) will no longer be recorded.',
        addedMessage: 'Scholar added to the roster.',
        addedMessageStale: 'Scholar added to the roster. Refresh to see it in the list.',
        couldNotAddTitle: 'Could Not Add',
        membershipEndedTitle: 'Membership Ended',
        membershipEndedMessage: "{scholar}'s membership was ended. Past reviews are unaffected.",
        couldNotEndTitle: 'Could Not End Membership',
        addStatus: {
          forbidden: 'You do not have permission to add scholars to this roster.',
          fallback: 'Could not add this scholar to the roster. Please try again.',
        },
        removeStatus: {
          forbidden: 'You do not have permission to change this roster.',
          notFound:
            'This membership could not be ended. It may already be closed, or this client may no longer be visible to you.',
          fallback: 'Could not end this membership. Please try again.',
        },
        signoffNever:
          'This roster has not been reviewed. Review requests cannot be sent for this client until someone confirms it is correct.',
        signoffStale:
          'This roster was reviewed on {at} and has changed since. Review requests cannot be sent until it is confirmed again.',
        signoffCurrent: 'Roster reviewed on {at}.',
        signoffAction: 'Confirm this roster is correct',
        signoffConfirmTitle: 'Confirm the roster',
        signoffConfirmBody:
          'Confirm that every scholar listed above belongs on this client’s Shariah Supervisory Board, and that nobody is missing. Review requests price against this list.',
        signoffFailed: 'The roster could not be confirmed. Please try again.',
      },

      products: {
        breadcrumb: 'Review Products',
        title: 'Review Products',
        titleFor: 'Review Products — {client}',
        /** See `roster.sectionTitle` — the same role, on the sibling
         *  sub-section. */
        sectionTitle: 'Review Products',
        loadError: "Could not load this client's products. Please try again.",
        intro:
          "Product assignment is who reviews this product. Board membership is the client's SSB roster. A scholar can be on either without the other.",
        classificationsLoadFailed:
          'Classifications could not be loaded, so products may show a classification number instead of its name. Refresh the page and try again.',
        activeSection: 'Active products',
        archivedSection: 'Archived products',
        emptyActive: 'No active products yet.',
        archivedNote:
          'Archived products stay here rather than being deleted, because certificates may still reference them. Restoring one makes it available for new work again.',
        newNameLabel: 'New product name',
        classificationOptionalLabel: 'Classification (optional)',
        classificationLabel: 'Classification',
        noClassification: 'No classification',
        classificationFallback: 'Classification #{id}',
        addProduct: 'Add product',
        adding: 'Adding…',
        nameLabel: 'Product name',
        uniquenessHint:
          'Product names only need to be unique for this client -- case does not matter.',
        rename: 'Rename',
        archive: 'Archive',
        restore: 'Restore',
        scholars: 'Scholars',
        hideScholars: 'Hide scholars',
        statusActive: 'Active',
        statusArchived: 'Archived',
        nameEmpty: 'Enter a product name.',
        nameTooLong: 'Product name must be 300 characters or fewer.',
        createdMessage: 'Product created.',
        createdMessageStale: 'Product created. Refresh to see it in the list.',
        couldNotCreateTitle: 'Could Not Create Product',
        updatedMessage: 'Product updated.',
        archivedTitle: 'Archived',
        restoredTitle: 'Restored',
        archivedMessage:
          'Product archived. It stays visible here because certificates may reference it.',
        restoredMessage: 'Product restored to active.',
        createStatus: {
          conflict:
            'A product with this name already exists for this client (case does not matter).',
          unprocessable: 'Enter a product name between 1 and 300 characters.',
          forbidden: 'You do not have permission to add products for this client.',
          fallback: 'Could not create this product. Please try again.',
        },
        updateStatus: {
          conflict:
            'A product with this name already exists for this client (case does not matter).',
          unprocessable: 'Enter a product name between 1 and 300 characters, and a valid status.',
          forbidden: 'You do not have permission to edit this product.',
          notFound: 'This product could not be found. It may be outside your sector.',
          fallback: 'Could not save this product. Please try again.',
        },
        scholarsPanel: {
          loading: 'Loading scholars…',
          loadError: "Could not load this product's scholars. Try collapsing and expanding again.",
          empty: 'No scholars are assigned to this product yet.',
          unassign: 'Unassign',
          removing: 'Removing…',
          reseedNote:
            'Unassigning takes effect immediately. If the roster & product seed is run again, it can silently restore this assignment.',
          assignLabel: 'Assign a scholar to this product',
          assign: 'Assign',
          assigning: 'Assigning…',
          allAssigned: 'Every scholar is already assigned to this product.',
          assignedTitle: 'Assigned',
          assignedMessage: 'Scholar assigned to this product.',
          couldNotAssignTitle: 'Could Not Assign',
          removedTitle: 'Removed',
          removedMessage: 'Scholar unassigned from this product.',
          couldNotUnassignTitle: 'Could Not Unassign',
          assignStatus: {
            forbidden: 'You do not have permission to assign scholars to this product.',
            notFound: 'This product could not be found. It may be outside your sector.',
            fallback: 'Could not assign this scholar. Please try again.',
          },
          unassignStatus: {
            forbidden: "You do not have permission to change this product's scholars.",
            notFound: 'This assignment could not be found. It may already have been removed.',
            fallback: 'Could not remove this scholar. Please try again.',
          },
        },
      },

      ...reviewRequestsBandsMessages,

      terms: {
        breadcrumb: 'Scholar Terms',
        // "Review Terms", matching the tab that hosts this panel on the
        // scholar form; the breadcrumb above keeps the older wording because
        // it labels the standalone route, which the composer still deep-links.
        // Both are standalone-only — embedded, the tab label and the form's
        // page heading say this already.
        title: 'Review Terms',
        titleFor: 'Review Terms — {scholar}',
        scholarNotFoundPanel:
          'This scholar could not be found. They may be outside your sector, or they may not exist.',
        loadError: "Could not load this scholar's terms. Please try again.",
        currentSection: 'Current terms',
        intro:
          "A scholar's arrangement is between them and SRB. It applies to every review they do, whichever client the review is for.",
        empty: 'No terms configured yet.',
        since: 'Since {from}',
        window: '{from} – {to}',
        change: 'Change terms',
        superAdminOnlyChange: "Only a super admin can change a scholar's arrangement.",
        addDefault: 'Add terms',
        superAdminOnlyDefault: "Only a super admin can set a scholar's arrangement.",
        historySection: 'History',
        historyNote:
          'Terms are append-only — closing a row keeps it here as the audit trail rather than deleting it.',
        emptyClosed: 'No closed terms yet.',
        fixedValueSummary: '{value} {currency}',
        quotaSummary: '{quota} reviews / cycle',
        quotaSummaryWithAnchor: '{quota} reviews / cycle, anchor day {anchorDay}',
        editor: {
          titleNew: 'New terms',
          titleChange: 'Change terms',
          replaceWarning:
            'This ends the current terms today and starts new ones. Past reviews keep the terms they were priced under.',
          arrangementLabel: 'Arrangement',
          valueLabel: 'Value',
          currencyLabel: 'Currency',
          quotaLabel: 'Reviews per cycle',
          anchorDayLabel: 'Anchor day (optional)',
          notesLabel: 'Notes (optional)',
          savedMessage: 'Terms updated.',
          couldNotCloseTitle: 'Could Not Close Terms',
          couldNotSaveTitle: 'Could Not Save Terms',
          /** Appended to the create-failure message when the old row was
           *  already closed by this same submit — leading space is
           *  deliberate, it joins onto the status sentence. */
          previousAlreadyClosed:
            ' The previous terms were already closed — add terms for this scholar again.',
          closedButNotSaved:
            'The previous terms were closed, but the new terms could not be saved. Add terms for this scholar again.',
          fixedValueInvalid: 'Enter a non-negative amount, e.g. 150.00.',
          quotaInvalid: 'Enter a quota greater than zero.',
          notesTooLong: 'Notes must be 2000 characters or fewer.',
        },
        createStatus: {
          conflict: 'This scholar already has open terms. Close them first.',
          forbidden: "You do not have permission to set a scholar's arrangement.",
          unprocessable: "Check this arrangement's required fields and try again.",
          fallback: 'Could not save these terms. Please try again.',
        },
        closeStatus: {
          forbidden: 'You do not have permission to close these terms.',
          notFound: 'These terms could not be found — they may already be closed.',
          fallback: 'Could not close the current terms. Please try again.',
        },
        arrangements: {
          srb_employee: {
            label: 'SRB employee',
            helper: 'Directly employed. Reviews are recorded at zero value.',
          },
          retainer_inclusive: {
            label: 'Retainer — review included',
            helper: 'The retainer covers the review fee.',
          },
          retainer_plus_fee: {
            label: 'Retainer + review fee',
            helper: 'A per-review fee applies on top of the retainer.',
          },
          fixed_value_review: {
            label: 'Fixed value per review',
            helper: 'Requires a value and a currency.',
          },
          zero_value_review: {
            label: 'Zero value',
            helper: 'Recorded, priced at zero.',
          },
          monthly_quota: {
            label: 'Monthly quota',
            helper:
              'A set number of reviews per cycle is included. Reviews beyond the quota are charged.',
          },
        },
      },

      ...reviewRequestsComposerMessages,
    },
  },
}
